/**
 * The bundle export on the main thread (decision 7; part 24a, table K): the open chapter's typing saved, a bundle worker
 * started for the book, its progress shown, and the finished zip offered to save. The worker reads the store and
 * compresses, so editing goes on while a book is bundled (decision 14). Cancelling ends the worker, which keeps nothing.
 *
 * The workspace's request, the worker and the browser's object URLs are injected, so the logic runs in tests.
 */
import { BUNDLE_ARCHIVE } from '@cockatrice/core';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import { fileName } from '../export/controller.ts';
import { readReply } from './protocol.ts';
import type { BundleFailure, BundleJob } from './protocol.ts';

/** What the bundle shows (K-rows' outcomes). */
export type BundleState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'exporting'; readonly done: number; readonly total: number }
  /**
   * `tooMany` (part 24c, part 17's deferred L3): it holds more files than an import reads, so it can't come back whole.
   * `bookId`, `sha256` and `at` (part 24e, T8): the book, the file's digest and when it was made, recorded as a backup once
   * the file is saved.
   */
  | { readonly kind: 'ready'; readonly url: string; readonly name: string; readonly size: number; readonly unreadable: number; readonly unsaved: boolean; readonly tooMany: boolean; readonly bookId: string; readonly sha256: string; readonly at: number }
  | { readonly kind: 'failed'; readonly reason: BundleFailure };

/** The book to bundle (table N'), once the open chapter's typing has been saved, or couldn't be (`unsaved`). */
export interface BundleRequest {
  readonly bookId: string;
  readonly device: string;
  readonly title: string;
  readonly unsaved: boolean;
}

/** A bundle worker, as far as the controller drives it (a `Worker`). */
export interface BundleWorker {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: BundleJob): void;
  terminate(): void;
}

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/** What the controller needs; everything with an effect is injected. */
export interface BundleOptions {
  /** The open book's request (the workspace's `bundleRequest`), or null with no book open. */
  readonly request: () => Promise<BundleRequest | null>;
  /** Starts a bundle worker. */
  readonly worker: () => BundleWorker;
  /** An object URL for the file (`URL.createObjectURL`), and what lets it go. */
  readonly objectUrl: (file: Blob) => string;
  readonly revokeUrl: (url: string) => void;
  /** Milliseconds since the Unix epoch (part 24e: when a bundle was made). */
  readonly now: () => number;
}

/** The bundle export's state and its one worker. */
export class BundleController {
  readonly state: Accessor<BundleState>;
  readonly #setState: (state: BundleState) => void;
  readonly #options: BundleOptions;
  /** The bundle under way: a new number for each, so what an earlier one sends is ignored. */
  #job = 0;
  #worker: BundleWorker | null = null;

  constructor(options: BundleOptions) {
    this.#options = options;
    const [state, setState] = createSignal<BundleState>({ kind: 'idle' });
    this.state = state;
    this.#setState = setState;
  }

  /** K1, K2: bundles the book as it is once the open chapter is saved; nothing while one is under way. */
  start(): void {
    const kind = this.state().kind;
    if (kind === 'saving' || kind === 'exporting') return;
    this.#forget();
    this.#job += 1;
    const job = this.#job;
    this.#setState({ kind: 'saving' });
    this.#options.request().then(
      (request) => {
        if (job !== this.#job) return;
        // K7: no book open (the button is only there with one).
        if (request === null) this.#setState({ kind: 'idle' });
        else this.#run(job, request);
      },
      () => {
        this.#end(job, { kind: 'failed', reason: 'failed' });
      },
    );
  }

  /** K4–K6: stops the bundle under way, keeping nothing of it; a file offered stays offered. */
  cancel(): void {
    const kind = this.state().kind;
    if (kind !== 'saving' && kind !== 'exporting') return;
    this.#job += 1;
    this.#end(this.#job, { kind: 'idle' });
  }

  /** K9: puts the offer or the failure away. */
  dismiss(): void {
    const kind = this.state().kind;
    if (kind !== 'ready' && kind !== 'failed') return;
    this.#forget();
    this.#setState({ kind: 'idle' });
  }

  /** K10: the app closing: nothing under way, nothing kept. */
  stop(): void {
    this.cancel();
    this.dismiss();
  }

  /** K1, K3, K8, K12: starts the worker on the request and follows what it sends while it is the bundle under way. */
  #run(job: number, request: BundleRequest): void {
    let worker: BundleWorker;
    try {
      worker = this.#options.worker();
    } catch {
      this.#end(job, { kind: 'failed', reason: 'failed' });
      return;
    }
    this.#worker = worker;
    const fail = (): void => {
      this.#end(job, { kind: 'failed', reason: 'failed' });
    };
    worker.addEventListener('error', fail);
    worker.addEventListener('messageerror', fail);
    worker.addEventListener('message', (event) => {
      if (job !== this.#job) return;
      const reply = readReply(dataOf(event));
      if (reply === null) fail();
      else if (reply.kind === 'progress') this.#setState({ kind: 'exporting', done: reply.done, total: reply.total });
      else if (reply.kind === 'failed') this.#end(job, reply);
      else {
        const { file, unreadable, files, sha256 } = reply;
        const tooMany = files > BUNDLE_ARCHIVE.files;
        this.#end(job, { kind: 'ready', url: this.#options.objectUrl(file), name: fileName(request.title, 'zip'), size: file.size, unreadable, unsaved: request.unsaved, tooMany, bookId: request.bookId, sha256, at: this.#options.now() });
      }
    });
    this.#setState({ kind: 'exporting', done: 0, total: 0 });
    worker.postMessage({ kind: 'bundle', bookId: request.bookId, device: request.device });
  }

  /** K4, K5, K8, K13: ends the bundle `job` with `state`, the worker with it; nothing for a job no longer under way. */
  #end(job: number, state: BundleState): void {
    if (job !== this.#job) return;
    this.#worker?.terminate();
    this.#worker = null;
    this.#setState(state);
  }

  /** K1, K9, K10: lets go of a file offered. */
  #forget(): void {
    const state = this.state();
    if (state.kind === 'ready') this.#options.revokeUrl(state.url);
  }
}
